import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { Zap, Sliders } from "lucide-react";

/**
 * DCA Strategy
 *
 * Source of truth: `dcaData` prop (lives in FinancialDashboard, autosaves to Firestore).
 * No internal useState/useEffect — every user action pushes to parent immediately,
 * which prevents the previous bug where mounting the component would clobber saved
 * manual entries with auto-computed values.
 */
const DCATab = ({ netIncomeData, dcaData, setDcaData }) => {
  const autoDcaEnabled = dcaData.autoDcaEnabled ?? true;
  const autoPercent = dcaData.autoPercent ?? 20;

  // Derive manual history — fall back to seeded zeros if nothing saved yet.
  const manualHistory =
    dcaData.history && dcaData.history.length
      ? dcaData.history
      : netIncomeData.map((r) => ({ month: r.month, dcaContribution: 0 }));

  // Compute auto values inline — never overwrite saved manual history.
  const autoHistory = netIncomeData.map((r) => ({
    month: r.month,
    dcaContribution: Math.max(0, (r.netIncome || 0) * (autoPercent / 100)),
  }));

  const displayHistory = autoDcaEnabled ? autoHistory : manualHistory;

  const totalNet = netIncomeData.reduce((s, r) => s + (r.netIncome || 0), 0);
  const avgNet = totalNet / (netIncomeData.length || 1);
  const targetMonthly = avgNet * (autoPercent / 100);

  /* ─── number formatting helpers ──────────────────────────────────────── */
  const fmtCurrency = (n) =>
    `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  /* ─── handlers — every change pushes to parent ───────────────────────── */
  const toggleAuto = () => {
    const next = !autoDcaEnabled;
    setDcaData({
      ...dcaData,
      autoDcaEnabled: next,
      autoPercent,
      history: next ? autoHistory : manualHistory,
      monthlyAmount: next ? targetMonthly : 0,
    });
  };

  const updatePercent = (value) => {
    const pct = Math.max(0, Math.min(100, parseFloat(value) || 0));
    const newAuto = netIncomeData.map((r) => ({
      month: r.month,
      dcaContribution: Math.max(0, (r.netIncome || 0) * (pct / 100)),
    }));
    setDcaData({
      ...dcaData,
      autoPercent: pct,
      ...(autoDcaEnabled && {
        history: newAuto,
        monthlyAmount: avgNet * (pct / 100),
      }),
    });
  };

  const updateManualDca = (month, value) => {
    const updated = manualHistory.map((entry) =>
      entry.month === month
        ? { ...entry, dcaContribution: parseFloat(value) || 0 }
        : entry
    );
    setDcaData({
      ...dcaData,
      autoDcaEnabled: false, // editing implicitly switches to manual
      history: updated,
      monthlyAmount: 0,
    });
  };

  /* ─── render ─────────────────────────────────────────────────────────── */
  return (
    <div className="surface p-6 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <p className="eyebrow mb-2">Strategy</p>
        <h2 className="headline text-2xl mb-2">Dollar-Cost Averaging</h2>
        <p className="text-arc-muted text-sm leading-relaxed max-w-2xl">
          Automatically allocate a percentage of your monthly profit to DCA, or
          manually set each month. Your changes save automatically.
        </p>
      </div>

      {/* Mode toggle + percent input */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 rounded-2xl border border-arc-line bg-white/[0.02]">
        <button
          onClick={toggleAuto}
          className={`btn-tactile flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
            autoDcaEnabled
              ? "bg-gradient-to-b from-arc-light to-arc-gold text-arc-bg border-arc-deep shadow-glow"
              : "bg-white/[0.03] text-arc-muted border-arc-line hover:text-arc-ink"
          }`}
        >
          <Zap size={15} strokeWidth={autoDcaEnabled ? 2.5 : 1.75} />
          Auto
        </button>
        <button
          onClick={toggleAuto}
          className={`btn-tactile flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
            !autoDcaEnabled
              ? "bg-gradient-to-b from-arc-light to-arc-gold text-arc-bg border-arc-deep shadow-glow"
              : "bg-white/[0.03] text-arc-muted border-arc-line hover:text-arc-ink"
          }`}
        >
          <Sliders size={15} strokeWidth={!autoDcaEnabled ? 2.5 : 1.75} />
          Manual
        </button>

        <div className="flex items-center gap-2 ml-auto">
          <span className="eyebrow">Auto %</span>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            value={autoPercent}
            onChange={(e) => updatePercent(e.target.value)}
            className="w-20 px-3 py-2 rounded-lg text-sm num text-right"
            disabled={!autoDcaEnabled}
          />
          <span className="text-arc-muted text-sm">of monthly profit</span>
        </div>
      </div>

      {/* Per-month table — always visible. Editable in manual mode. */}
      <div className="overflow-x-auto mb-6 rounded-2xl border border-arc-line">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th className="text-left px-4 py-3">Month</th>
              <th className="text-right px-4 py-3">Net Income</th>
              <th className="text-right px-4 py-3">Auto DCA</th>
              <th className="text-right px-4 py-3">Manual DCA</th>
            </tr>
          </thead>
          <tbody>
            {netIncomeData.map((r) => {
              const auto = Math.max(0, (r.netIncome || 0) * (autoPercent / 100));
              const manualValue =
                manualHistory.find((m) => m.month === r.month)
                  ?.dcaContribution || 0;
              return (
                <tr key={r.month}>
                  <td className="px-4 py-2.5 font-medium text-arc-ink">{r.month}</td>
                  <td className="px-4 py-2.5 text-right num text-arc-ink">
                    {fmtCurrency(r.netIncome)}
                  </td>
                  <td
                    className={`px-4 py-2.5 text-right num ${
                      autoDcaEnabled ? "text-arc-gold font-medium" : "text-arc-dim"
                    }`}
                  >
                    {fmtCurrency(auto)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <input
                      type="number"
                      min={0}
                      step={1}
                      value={manualValue}
                      onChange={(e) =>
                        updateManualDca(r.month, e.target.value)
                      }
                      className={`w-28 px-2 py-1 rounded-md text-sm num text-right ${
                        !autoDcaEnabled
                          ? "text-arc-ink"
                          : "text-arc-dim"
                      }`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Chart */}
      <div className="rounded-2xl border border-arc-line bg-white/[0.02] p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="eyebrow">
            {autoDcaEnabled ? "Auto Projection" : "Manual Plan"}
          </span>
          <span className="text-xs font-mono text-arc-muted">
            {displayHistory.length} months
          </span>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={displayHistory}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="month" stroke="#a1a1aa" />
            <YAxis
              stroke="#a1a1aa"
              tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
            />
            <Tooltip
              contentStyle={{
                background: "#17171a",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "0.75rem",
                color: "#fafafa",
              }}
              formatter={(value) => [fmtCurrency(value), "DCA"]}
            />
            <Line
              type="monotone"
              dataKey="dcaContribution"
              stroke="#c4b287"
              strokeWidth={2.5}
              dot={{ fill: "#c4b287", r: 3 }}
              activeDot={{ r: 5, fill: "#d4c79e" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Summary footer */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl border border-arc-line bg-white/[0.02]">
          <p className="eyebrow mb-1">Avg Net Income</p>
          <p className="text-xl num text-arc-ink">{fmtCurrency(avgNet)}</p>
        </div>
        <div className="p-4 rounded-2xl border border-arc-line bg-white/[0.02]">
          <p className="eyebrow mb-1">Target DCA / mo</p>
          <p className="text-xl num text-arc-gold">{fmtCurrency(targetMonthly)}</p>
        </div>
        <div className="p-4 rounded-2xl border border-arc-line bg-white/[0.02]">
          <p className="eyebrow mb-1">Mode</p>
          <p className="text-xl num text-arc-ink">
            {autoDcaEnabled ? `Auto · ${autoPercent}%` : "Manual"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DCATab;
